import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { useGetWorkflow, useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowDetailsUrl, workflowsUrl } from "@/utils/automations";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationDefinitionSectionEdit } from "@/routes/automations/automation-edit-page/AutomationDefinitionSectionEdit";
import { SaveAutomationStepsButton } from "@/routes/automations/automation-edit-page/SaveAutomationStepsButton";
import { useSetAtom, useStore } from "jotai";
import { editableStepsAtom, isEditingStepsAtom } from "@/routes/automations/automation-details-page/state";
import {
    convertWorkflowActionToFormSteps,
    mergeFormDataIntoWorkflow,
    type WorkflowEditFormData,
} from "@/components/automations/automation-edit-form-data";
import { newWorkflowFromTemplate, type AutomationTemplate } from "@/utils/automation-templates";
import CreateAutomationButton from "@/routes/automations/automation-edit-page/CreateAutomationButton";
import { AutomationEditableHeader } from "@/routes/automations/automation-edit-page/AutomationEditableHeader";
import AutomationEditDialog from "@/components/automations/AutomationEditDialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type AutomationEditPageProps = {
    template?: AutomationTemplate;
};

export const AutomationEditPage: FC<AutomationEditPageProps> = ({ template }) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [workflow, setWorkflow] = useState<PlainWorkflow | undefined>(undefined);
    const navigate = useNavigate();
    const { toast } = useToast();

    const { workflowId } = useParams<{ workflowId: string }>();
    const { data: workflowData, error: workflowError } = useGetWorkflow(template ? undefined : workflowId);
    const { mutateAsync: updateWorkflow } = useUpdateWorkflow();

    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const store = useStore();
    const hasInitializedSteps = useRef(false);

    useEffect(() => {
        if (template) {
            setWorkflow(newWorkflowFromTemplate(template));
        } else if (workflowId) {
            setWorkflow(workflowData);
        }
    }, [workflowData, template, workflowId]);

    useEffect(() => {
        setIsEditingSteps(true);
        // Convert the workflow action to editable steps only on initial load.
        // Skip if we've already initialized to preserve in-memory step edits
        // when the workflow updates (e.g., after saving trigger properties).
        if (hasInitializedSteps.current) {
            return;
        }
        if (workflow?.spec?.action) {
            setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec.action));
            hasInitializedSteps.current = true;
        } else if (workflow) {
            setEditableSteps(null);
            hasInitializedSteps.current = true;
        }
    }, [workflow, setEditableSteps, setIsEditingSteps]);

    const handleCancel = useCallback(() => {
        setIsEditingSteps(true);
        setEditableSteps(null);
        if (template) {
            navigate(workflowsUrl());
        }
        if (workflowId) {
            navigate(workflowDetailsUrl({ id: workflowId }));
        }
    }, [navigate, setEditableSteps, setIsEditingSteps, template, workflowId]);

    // Handler for AutomationEditDialog (trigger properties)
    // The dialog edits executor, triggers, and limits - NOT steps.
    // Steps are edited inline on the page via editableStepsAtom.
    // We must preserve the current editable steps when merging dialog form data.
    const saveWorkflowFormData = (formData: WorkflowEditFormData) => {
        if (workflow) {
            const currentEditableSteps = store.get(editableStepsAtom);
            const formDataWithCurrentSteps: WorkflowEditFormData = {
                ...formData,
                steps: currentEditableSteps ?? formData.steps,
            };
            setWorkflow(mergeFormDataIntoWorkflow(workflow, formDataWithCurrentSteps));
            setIsEditDialogOpen(false);
        }
    };

    // Handler for AutomationEditableHeader (inline name/description editing)
    const handleHeaderUpdate = useCallback(
        async (name: string, description: string) => {
            if (template && workflow) {
                // Creating new automation: update local state
                setWorkflow({
                    ...workflow,
                    metadata: {
                        ...workflow.metadata,
                        name,
                        description,
                    },
                });
            } else if (workflowId) {
                // Editing existing automation: call API
                try {
                    await updateWorkflow({
                        workflowId,
                        name,
                        description,
                    });
                } catch (error) {
                    toast({
                        title: "Failed to update automation header",
                        description: formatError(error),
                    });
                    throw error;
                }
            }
        },
        [template, workflow, workflowId, updateWorkflow, toast],
    );

    return (
        <div data-testid="workflow-edit-page" className="flex size-full flex-col">
            <div className="flex flex-col p-[26px]">
                {workflow ? (
                    <div className="flex flex-col gap-2">
                        {/* Header row: title + action buttons */}
                        <div className="flex items-start justify-between gap-8">
                            <AutomationEditableHeader
                                workflow={workflow}
                                onUpdate={handleHeaderUpdate}
                                onEditProperties={() => setIsEditDialogOpen(true)}
                            />
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                                        Cancel
                                    </Button>
                                    {template ? (
                                        <CreateAutomationButton workflow={workflow} />
                                    ) : (
                                        <SaveAutomationStepsButton workflowId={workflow.id} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <BreadcrumbsSkeleton itemsCount={2} />
                )}
                <ErrorMessage error={workflowError} />
            </div>

            {workflow && <AutomationDefinitionSectionEdit />}

            <AutomationEditDialog
                open={isEditDialogOpen}
                onOpenChange={setIsEditDialogOpen}
                workflow={workflow}
                onSubmit={template ? saveWorkflowFormData : undefined}
            />
        </div>
    );
};
